import { company } from '~/company/Company';

import { groupByItemList } from '../itemList';

export type YandexEcommerceAction =
  | 'detail'
  | 'add'
  | 'remove'
  | 'click'
  | 'purchase';

export interface YandexEcommerceProduct {
  id: string;
  name: string;
  price?: number;
  discount?: number;
  quantity?: number;
  category?: string;
  coupon?: string;
  list?: string;
  position?: number;
}

export interface YandexEcommercePayload {
  ecommerce: {
    currencyCode: string;
    [action: string]: unknown;
  };
}

const FEED_OFFER_ID_UNSUPPORTED = /[^A-Za-z0-9.,-]/g;

export const toFeedOfferId = (sku: unknown): string =>
  String(sku ?? '').replace(FEED_OFFER_ID_UNSUPPORTED, '');

const toNumber = (value: unknown): number | undefined => {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed)
    ? parsed
    : undefined;
};

const toProduct = (
  item: Record<string, unknown>,
  listed: boolean,
): YandexEcommerceProduct => {
  const product: YandexEcommerceProduct = {
    id: toFeedOfferId(item.item_id),
    name: String(item.item_name ?? ''),
  };

  const price = toNumber(item.price);
  const discount = toNumber(item.discount);
  if (price !== undefined) {
    product.price = discount ? Math.max(price - discount, 0) : price;
  }

  if (discount) {
    product.discount = discount;
  }

  if (item.item_category) {
    product.category = String(item.item_category);
  }

  if (listed) {
    if (item.item_list_name) {
      product.list = String(item.item_list_name);
    }

    const index = toNumber(item.index);
    if (index !== undefined) {
      product.position = index + 1;
    }

    return product;
  }

  const quantity = toNumber(item.quantity);
  if (quantity !== undefined) {
    product.quantity = quantity;
  }

  if (item.coupon) {
    product.coupon = String(item.coupon);
  }

  return product;
};

const toProducts = (
  items: unknown,
  listed = false,
): YandexEcommerceProduct[] =>
  Array.isArray(items)
    ? items
        .filter(
          (item): item is Record<string, unknown> =>
            typeof item === 'object' && item !== null,
        )
        .map((item) => toProduct(item, listed))
        .filter(({ id }) => Boolean(id))
    : [];

export const toYandexEcommerce =
  (action: Exclude<YandexEcommerceAction, 'purchase'>) =>
  (payload: Record<string, unknown>): Record<string, unknown> => {
    const result: YandexEcommercePayload = {
      ecommerce: {
        currencyCode: company.currencyCode,
        [action]: { products: toProducts(payload.items, action === 'click') },
      },
    };

    return result as unknown as Record<string, unknown>;
  };

/**
 * `impressions` is the one action Metrika takes as a flat product array —
 * every other action nests them under `products`.
 */
export const toYandexImpressions = (
  queue: Record<string, unknown>[],
): Record<string, unknown>[] =>
  groupByItemList(queue)
    .map(({ items }) => toProducts(items, true))
    .filter((products) => products.length > 0)
    .map((impressions) => ({
      ecommerce: { currencyCode: company.currencyCode, impressions },
    }));

export const toYandexPurchase = (
  payload: Record<string, unknown>,
): Record<string, unknown> => {
  const actionField: Record<string, unknown> = {
    id: String(payload.transaction_id ?? ''),
  };

  const revenue = toNumber(payload.value);
  if (revenue !== undefined) {
    actionField.revenue = revenue;
  }

  if (payload.coupon) {
    actionField.coupon = String(payload.coupon);
  }

  const result: YandexEcommercePayload = {
    ecommerce: {
      currencyCode: company.currencyCode,
      purchase: {
        actionField,
        products: toProducts(payload.items),
      },
    },
  };

  return result as unknown as Record<string, unknown>;
};
